import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays as CalIcon } from "lucide-react";
import { fmtCurrency } from "../lib/api";

// Color cycle for differentiating priceIDs visually. Labels come straight from RA.
const COLOR_VARS = {
  navy:        "var(--aq-navy)",
  "navy-mid":  "var(--aq-navy-mid)",
  gold:        "var(--aq-gold)",
  "warm-gold": "var(--aq-gold-warm)",
};
const DEFAULT_COLORS = ["navy", "gold", "navy-mid", "warm-gold"];

function shortHM(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  if (m === 0) return `${h}`;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function tripLabel(row) {
  return row.priceDisplayName || row.priceName || row.serviceName || "Charter";
}

function tripColor(row, fallbackIdx = 0) {
  return COLOR_VARS[DEFAULT_COLORS[fallbackIdx % DEFAULT_COLORS.length]];
}

// Group raw availability rows by `YYYY-MM-DD` then sort each bucket by validFromTime asc.
export function groupByDate(rows) {
  const out = {};
  if (!Array.isArray(rows)) return out;
  for (const r of rows) {
    const d = String(r.date || "").slice(0, 10);
    if (!d) continue;
    (out[d] ||= []).push(r);
  }
  for (const d in out) {
    out[d].sort((a, b) => (a.validFromTime || "").localeCompare(b.validFromTime || ""));
  }
  return out;
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function todayISO() { return new Date().toISOString().slice(0, 10); }
function fmtMonthLabel(d) {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
function isoFromYMD(y, m, day) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Calendar({ rows, onPickSlot }) {
  const today = todayISO();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const byDate = useMemo(() => groupByDate(rows || []), [rows]);
  const priceIDs = useMemo(() => {
    const ids = new Set();
    for (const list of Object.values(byDate)) for (const r of list) ids.add(r.priceID);
    return Array.from(ids).sort((a, b) => a - b);
  }, [byDate]);
  // map priceID → cycle index for unknown-color fallback
  const idxOf = useMemo(() => {
    const m = new Map();
    priceIDs.forEach((id, i) => m.set(id, i));
    return m;
  }, [priceIDs]);

  // Build the 6-row × 7-col grid for the cursor month
  const cells = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const firstWeekday = first.getDay(); // 0..6
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    // leading blanks
    for (let i = 0; i < firstWeekday; i++) cells.push({ blank: true, key: `b${i}` });
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = isoFromYMD(y, m, d);
      cells.push({ blank: false, key: iso, iso, day: d });
    }
    // trailing blanks to fill grid to 42 (6 rows)
    while (cells.length < 42) cells.push({ blank: true, key: `t${cells.length}` });
    return cells;
  }, [cursor]);

  const goPrev = () => setCursor((c) => addMonths(c, -1));
  const goNext = () => setCursor((c) => addMonths(c, 1));
  const goToday = () => setCursor(startOfMonth(new Date()));

  const totalOfferings = Object.values(byDate).reduce((sum, list) => sum + list.length, 0);

  return (
    <div className="aq-card p-5 md:p-6" data-testid="calendar-root">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalIcon size={18} style={{ color: "var(--aq-gold)" }} />
          <h3 className="aq-serif text-xl sm:text-2xl" style={{ color: "var(--aq-navy)" }} data-testid="calendar-month-label">
            {fmtMonthLabel(cursor)}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="calendar-prev" onClick={goPrev} aria-label="Previous month"
            className="aq-date-pill" style={{ padding: "6px 10px" }}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" data-testid="calendar-today" onClick={goToday}
            className="aq-date-pill" style={{ padding: "6px 14px", fontSize: 12 }}>
            Today
          </button>
          <button type="button" data-testid="calendar-next" onClick={goNext} aria-label="Next month"
            className="aq-date-pill" style={{ padding: "6px 10px" }}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <p className="text-xs mt-3" style={{ color: "var(--aq-text-soft)" }} data-testid="calendar-summary">
        {totalOfferings} bookable slot{totalOfferings === 1 ? "" : "s"} across the next 90 days. Click a slot to reserve.
      </p>

      {/* Weekday header */}
      <div className="grid grid-cols-7 mt-5 gap-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "var(--aq-text-soft)" }}>
        {WEEKDAYS.map((w) => <div key={w} className="py-1 text-center">{w}</div>)}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1 mt-1" data-testid="calendar-grid">
        {cells.map((c) => {
          if (c.blank) return <div key={c.key} aria-hidden style={{ minHeight: 92 }} />;
          const inPast = c.iso < today;
          const list = byDate[c.iso] || [];
          const isToday = c.iso === today;
          return (
            <div
              key={c.key}
              data-testid={`cal-day-${c.iso}`}
              className="rounded-sm relative"
              style={{
                background: inPast ? "transparent" : "#fff",
                border: `1px solid ${isToday ? "var(--aq-gold)" : "#ebe6d4"}`,
                opacity: inPast ? 0.45 : 1,
                minHeight: 92,
                padding: 4,
                display: "flex", flexDirection: "column", gap: 3,
              }}
            >
              <div
                className="text-[11px] font-semibold leading-none px-1 pt-1"
                style={{ color: inPast ? "var(--aq-text-soft)" : (list.length ? "var(--aq-navy)" : "var(--aq-text-soft)") }}
              >
                {c.day}
              </div>
              {!inPast && list.map((row, i) => {
                const color = tripColor(row, idxOf.get(row.priceID) ?? i);
                const label = tripLabel(row);
                const range = `${shortHM(row.validFromTime)}–${shortHM(row.validToTime)}`;
                const price = fmtCurrency(row.amount);
                return (
                  <button
                    key={`${c.iso}-${row.priceID}-${i}`}
                    type="button"
                    onClick={() => onPickSlot?.(c.iso, row)}
                    data-testid={`cal-slot-${c.iso}-${row.priceID}`}
                    title={`${label} · ${range} · ${price}`}
                    className="text-left w-full"
                    style={{
                      borderLeft: `3px solid ${color}`,
                      background: "rgba(11, 30, 63, 0.04)",
                      borderRadius: 2,
                      padding: "4px 6px",
                      lineHeight: 1.15,
                      transition: "background-color 160ms ease, transform 160ms ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(201, 162, 74, 0.16)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(11, 30, 63, 0.04)"; }}
                  >
                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-[11px] font-semibold tracking-tight truncate" style={{ color: "var(--aq-navy)" }}>{label}</span>
                      <span className="text-[10px] font-semibold whitespace-nowrap" style={{ color }}>{price}</span>
                    </div>
                    <div className="text-[10px] font-mono" style={{ color: "var(--aq-text-soft)" }}>{range}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      {priceIDs.length > 0 && (
        <div className="flex flex-wrap gap-3 items-center mt-5 text-[11px]" data-testid="calendar-legend" style={{ color: "var(--aq-text-soft)" }}>
          {priceIDs.map((id, i) => {
            const sample = Object.values(byDate).flat().find((r) => r.priceID === id);
            if (!sample) return null;
            return (
              <span key={id} className="inline-flex items-center gap-2">
                <span style={{ width: 10, height: 10, background: tripColor(sample, i), display: "inline-block", borderRadius: 1 }} />
                <span>{tripLabel(sample)}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
